import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal } from '@feathr/blackbox';
import { showError, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { DEFAULT_GOAL_NAME, getGoalNameErrors, getValidationErrors } from './GoalDrawer.utils';

export interface IUseGoalDrawerFormReturn {
  goal: Goal | null;
  isSaving: boolean;
  isValid: boolean;
  /** Errors shown inline below the name field. */
  nameErrors: string[];
  /** Errors shown on the submit button. */
  buttonErrors: string[];
  handleSave: () => Promise<void>;
  reset: () => void;
}

/**
 * Manages ephemeral goal creation, validation, and saving for the GoalDrawer.
 *
 * @param onClose - Callback to close the drawer.
 * @param isOpen - Whether the drawer is currently open.
 * @returns Form state and handlers for the GoalDrawer.
 */
export function useGoalDrawerForm(onClose: () => void, isOpen: boolean): IUseGoalDrawerFormReturn {
  const { t } = useTranslation();
  const { Goals } = useStore();

  const [goal, setGoal] = useState<Goal | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setGoal(
        Goals.create({
          kind: 'account',
          name: DEFAULT_GOAL_NAME,
        }),
      );
    }
  }, [isOpen]);

  const currentGoalName = goal?.get('name') ?? '';

  const allNameErrors = useMemo(() => {
    if (!goal) {
      return [];
    }
    const existingNames = Goals.list()
      .models.filter((g) => !g.isEphemeral)
      .map((g) => g.get('name'));

    return getGoalNameErrors(currentGoalName, existingNames, t);
  }, [goal, currentGoalName]);

  const isPlaceholderName = currentGoalName === DEFAULT_GOAL_NAME;
  const nameErrors = isPlaceholderName ? [] : allNameErrors;

  const isGoalModelValid = goal?.isValid() ?? false;
  const isValid = goal !== null && allNameErrors.length === 0 && isGoalModelValid;

  const buttonErrors = getValidationErrors(allNameErrors, goal);

  const reset = useCallback((): void => {
    if (goal) {
      Goals.removeEphemeral(goal.id);
    }
    setGoal(null);
  }, [goal, Goals]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!goal) {
      return;
    }

    setIsSaving(true);

    try {
      await Goals.add(goal);
      toast(
        t("Goal '{{- name}}' created — now available to select in campaigns and flights.", {
          name: goal.get('name'),
        }),
        { type: 'success' },
      );
      setGoal(null);
      onClose();
    } catch (error) {
      // TODO: surface server-side name uniqueness errors inline once anhinga#7380 adds validation
      showError(error, t);
    } finally {
      setIsSaving(false);
    }
  }, [goal, onClose, t]);

  return {
    goal,
    isSaving,
    isValid,
    nameErrors,
    buttonErrors,
    handleSave,
    reset,
  };
}
